import React, { useEffect, useMemo, useRef, useState } from 'react';
import TermsContent from '../components/TermsContent';
import { supabase } from '../lib/supabase';
import { authApi } from '../services/api';

const reserved = new Set(['admin', 'administrator', 'root', 'mod', 'moderator', 'support', 'help', 'api', 'null', 'undefined', 'convoloc', 'loconvo']);

function validateUsername(u: string): string | null {
  const name = u.trim();
  if (name.length < 3 || name.length > 20) return '3–20 characters';
  if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(name)) return 'Start with a letter; letters/numbers/_ only';
  if (/__/.test(name) || /_$/.test(name)) return 'No double or trailing underscore';
  if (/^\d+$/.test(name)) return 'Cannot be only digits';
  if (reserved.has(name.toLowerCase())) return 'Reserved username';
  return null;
}

function calcAge(dob: string): number | null {
  const dt = new Date(dob);
  if (isNaN(dt.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - dt.getFullYear();
  const m = now.getMonth() - dt.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dt.getDate())) age--;
  return age;
}

const RegisterPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [username, setUsername] = useState('');
  const [dob, setDob] = useState(''); // YYYY-MM-DD
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [usernameStatus, setUsernameStatus] = useState<{ ok: boolean; msg?: string }>({ ok: false });
  const [emailStatus, setEmailStatus] = useState<{ ok: boolean; msg?: string }>({ ok: false });
  const [offerSignin, setOfferSignin] = useState(false);
  const [emailNeedsConfirm, setEmailNeedsConfirm] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [successNotice, setSuccessNotice] = useState<string | null>(null);
  const termsRef = useRef<HTMLDivElement | null>(null);

  const age = useMemo(() => calcAge(dob), [dob]);
  const checks = useMemo(() => {
    const len = password.length >= 8;
    const upper = /[A-Z]/.test(password);
    const lower = /[a-z]/.test(password);
    const digit = /\d/.test(password);
    const special = /[^A-Za-z0-9]/.test(password);
    const match = password.length > 0 && password === confirm;
    const score = [len, upper, lower, digit, special].filter(Boolean).length;
    const label = score <= 2 ? 'Weak' : score === 3 ? 'Fair' : score === 4 ? 'Good' : 'Strong';
    const color = score <= 2 ? '#ef4444' : score === 3 ? '#f59e0b' : score === 4 ? '#10b981' : '#059669';
    return { len, upper, lower, digit, special, match, score, label, color };
  }, [password, confirm]);

  useEffect(() => {
    let active = true;
    const run = async () => {
      const err = validateUsername(username);
      if (err || !username.trim()) { setUsernameStatus({ ok: false, msg: err || 'Enter a username' }); return; }
      try {
        const r = await authApi.checkUsername(username.trim());
        if (!active) return;
        setUsernameStatus(r.available ? { ok: true } : { ok: false, msg: 'Username taken' });
      } catch {
        if (!active) return;
        setUsernameStatus({ ok: false, msg: 'Unable to check' });
      }
    };
    const t = setTimeout(run, 350);
    return () => { active = false; clearTimeout(t); };
  }, [username]);

  // Live email existence check (best-effort)
  useEffect(() => {
    let active = true;
    const run = async () => {
      const val = email.trim();
      if (!val) { setEmailStatus({ ok: false, msg: 'Enter an email' }); return; }
      // Basic email pattern to avoid unnecessary calls
      const emailOk = /.+@.+\..+/.test(val);
      if (!emailOk) { setEmailStatus({ ok: false, msg: 'Invalid email' }); return; }
      try {
        const r = await authApi.checkEmail(val);
        if (!active) return;
        if (r.blocked) {
          const until = r.blockedUntil ? ` (wait until ${new Date(r.blockedUntil).toLocaleString()})` : '';
          setEmailStatus({ ok: false, msg: `Recently deleted. Please wait before re-registering${until}` });
        } else if (r.exists) setEmailStatus({ ok: false, msg: 'Email already registered — sign in instead' });
        else setEmailStatus({ ok: true });
      } catch {
        if (!active) return;
        setEmailStatus({ ok: true }); // don’t block submit if check fails
      }
    };
    const t = setTimeout(run, 400);
    return () => { active = false; clearTimeout(t); };
  }, [email]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setSuccessNotice(null);
    setOfferSignin(false);
    setEmailNeedsConfirm(false);
    if (!supabase) { setMessage('Auth not configured'); return; }
    if (!agree) { setMessage('Please read and accept the Terms to continue'); return; }
    if (!email.trim()) { setMessage('Email required'); return; }
    if (!checks.len) { setMessage('Password must be at least 8 characters'); return; }
    if (!checks.match) { setMessage('Passwords do not match'); return; }
    if (!dob) { setMessage('Date of birth required'); return; }
    if (age === null || age < 13) { setMessage('You must be at least 13'); return; }
    const uErr = validateUsername(username);
    if (uErr) { setMessage(`Username: ${uErr}`); return; }
    if (!usernameStatus.ok) { setMessage(usernameStatus.msg || 'Username unavailable'); return; }

    setMessage('Creating your account…');
    setSubmitting(true);
    try {
      // Re-check email status to enforce cooldowns before calling Supabase
      try {
        const latest = await authApi.checkEmail(email.trim());
        if (latest.blocked) {
          const until = latest.blockedUntil ? ` (wait until ${new Date(latest.blockedUntil).toLocaleString()})` : '';
          setMessage(`This email was recently deleted. Please wait before re-registering${until}.`);
          setSubmitting(false);
          return;
        }
        if (latest.exists) {
          setMessage('This email is already registered. You can sign in with your password or resend the confirmation email.');
          setOfferSignin(true);
          setEmailNeedsConfirm(true);
          setSubmitting(false);
          return;
        }
      } catch {
        // continue; if the check fails we don't block signup
      }

      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: { date_of_birth: dob, username: username.trim() },
        },
      });

      if (error) {
        // Common Supabase error when email is already registered (status 400)
        const msg = String(error?.message || '').toLowerCase();
        const already = (error?.status === 400) || msg.includes('already registered') || msg.includes('already exists') || msg.includes('user already');
        if (already) {
          setMessage('This email is already registered. You can sign in with your password or resend the confirmation email.');
          setOfferSignin(true);
          setEmailNeedsConfirm(true);
          return;
        }
        throw error;
      }

      if (data.session?.access_token) {
        try {
          await authApi.setUsername(username.trim());
          setMessage('Registration complete. Redirecting...');
          setTimeout(() => { window.location.replace('/'); }, 400);
        } catch (err: any) {
          setMessage(err?.response?.data?.error || 'Failed to set username');
        }
        return;
      }

      const { data: signData, error: signErr } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (!signErr && signData.session?.access_token) {
        try {
          await authApi.setUsername(username.trim());
          setMessage('Registration complete. Redirecting...');
          setTimeout(() => { window.location.replace('/'); }, 400);
        } catch (err: any) {
          setMessage(err?.response?.data?.error || 'Failed to set username');
        }
        return;
      }

      try { localStorage.setItem('desired_username', username.trim()); } catch { }
      setEmailNeedsConfirm(true);
      setSuccessNotice('Approval link sent! Please check your email to confirm your account before signing in.');
    } catch (err: any) {
      setMessage(err?.message || 'Failed to register');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSigninNow = async () => {
    if (!supabase) return;
    setActionBusy(true);
    setMessage(null);
    setSuccessNotice(null);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (error) {
        const msg = String(error.message || '').toLowerCase();
        if (msg.includes('email not confirmed') || msg.includes('confirm') || error.status === 400) {
          setEmailNeedsConfirm(true);
          setMessage('Please confirm your email before signing in.');
        } else {
          setMessage(error.message || 'Failed to sign in');
        }
        return;
      }
      if (data.session?.access_token) {
        try {
          await authApi.setUsername(username.trim());
        } catch { }
        window.location.replace('/');
      }
    } finally {
      setActionBusy(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!supabase) return;
    setActionBusy(true);
    setMessage(null);
    setSuccessNotice(null);
    try {
      const { error } = await supabase.auth.resend({ type: 'signup', email: email.trim() });
      if (error) throw error;
      setSuccessNotice('Approval link resent! Please check your inbox to confirm your account.');
    } catch (err: any) {
      setMessage(err?.message || 'Failed to resend confirmation');
    } finally {
      setActionBusy(false);
    }
  };

  // Handlers for terms modal
  const handleTermsScroll = () => {
    const el = termsRef.current;
    if (!el) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
    if (nearBottom) setScrolledToEnd(true);
  };

  if (!supabase) {
    return (
      <div style={{ maxWidth: 480, margin: '40px auto', fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}>
        <h2>Create Account</h2>
        <p>Supabase is not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY, then redeploy.</p>
      </div>
    );
  }

  return (
    <div className="auth-wrapper" style={{ fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}>
      <div className="auth-card">
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <h1 className="auth-title">Create Account</h1>
          <p className="auth-subtitle">Use your email and a strong password. You’ll set a public username.</p>
        </div>

        <div style={{ background: '#F3F4F6', border: '1px solid #E5E7EB', color: '#374151', padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
          After signing up, you’ll receive a verification email. Open it to confirm your address before signing in. If you don’t see it, check your spam/junk folder.
        </div>

        {message && (
          <div style={{ background: '#F3F4F6', border: '1px solid #E5E7EB', color: '#374151', padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>{message}</div>
        )}

        <form onSubmit={onSubmit} aria-busy={submitting}>
          <div style={{ display: 'grid', gap: 14 }}>
            {/* Email */}
            <div style={{ display: 'grid', gap: 8 }}>
              <label htmlFor="reg-email" style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>Email</label>
              <input id="reg-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required
                placeholder="your@email.com"
                disabled={submitting}
                style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 16, outline: 'none', boxSizing: 'border-box' }}
                onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
              />
              {email && (
                <div style={{ fontSize: 12, marginTop: 2, color: emailStatus.ok ? '#059669' : '#b91c1c' }}>
                  {emailStatus.ok ? 'Email looks good' : (emailStatus.msg || 'Invalid email')}
                </div>
              )}
            </div>

            {/* Password */}
            <div style={{ display: 'grid', gap: 8 }}>
              <label htmlFor="reg-pass" style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>Password</label>
              <input id="reg-pass" type="password" value={password} onChange={e => setPassword(e.target.value)} required
                placeholder="Password (min 8)"
                disabled={submitting}
                style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 16, outline: 'none', boxSizing: 'border-box' }}
                onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
              />
              {password && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0' }}>
                    <div style={{ flex: 1, height: 6, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ width: `${(checks.score / 5) * 100}%`, height: '100%', background: checks.color, transition: 'width 120ms' }} />
                    </div>
                    <span style={{ fontSize: 12, color: checks.color }}>{checks.label}</span>
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 2, fontSize: 12 }}>
                    <li style={{ color: checks.len ? '#059669' : '#6b7280' }}>• At least 8 characters</li>
                    <li style={{ color: checks.upper ? '#059669' : '#6b7280' }}>• Uppercase letter</li>
                    <li style={{ color: checks.lower ? '#059669' : '#6b7280' }}>• Lowercase letter</li>
                    <li style={{ color: checks.digit ? '#059669' : '#6b7280' }}>• Number</li>
                    <li style={{ color: checks.special ? '#059669' : '#6b7280' }}>• Special character</li>
                  </ul>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div style={{ display: 'grid', gap: 8 }}>
              <label htmlFor="reg-pass2" style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>Confirm password</label>
              <input id="reg-pass2" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required
                placeholder="Re-enter password"
                disabled={submitting}
                style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 16, outline: 'none', boxSizing: 'border-box' }}
                onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
              />
              {confirm && (
                <div style={{ fontSize: 12, color: checks.match ? '#059669' : '#b91c1c' }}>{checks.match ? 'Passwords match' : 'Passwords do not match'}</div>
              )}
            </div>

            {/* Username */}
            <div style={{ display: 'grid', gap: 8 }}>
              <label htmlFor="reg-username" style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>Username (public)</label>
              <input id="reg-username" type="text" value={username} onChange={e => setUsername(e.target.value)}
                placeholder="e.g. biker_joe"
                disabled={submitting}
                style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 16, outline: 'none', boxSizing: 'border-box' }}
                onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
              />
              <div style={{ fontSize: 12, marginTop: 2, color: usernameStatus.ok ? '#059669' : '#b91c1c' }}>{usernameStatus.ok ? 'Available' : (usernameStatus.msg || 'Invalid')}</div>
            </div>

            {/* Date of Birth */}
            <div style={{ display: 'grid', gap: 8 }}>
              <label htmlFor="reg-dob" style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>Date of birth</label>
              <input id="reg-dob" type="date" value={dob} onChange={e => setDob(e.target.value)} required
                disabled={submitting}
                style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 16, outline: 'none', boxSizing: 'border-box' }}
                onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
              />
              {dob && (age !== null) && (
                <div style={{ fontSize: 12, color: age >= 13 ? '#6b7280' : '#b91c1c' }}>{age >= 13 ? `Age: ${age}` : 'Must be at least 13'}</div>
              )}
            </div>

            {/* Terms */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <input type="checkbox" checked={agree} readOnly onClick={() => { setShowTerms(true); setScrolledToEnd(false); }} />
              <span style={{ color: '#374151', fontSize: 14 }}>I have read and agree to the Terms & Acceptable Use</span>
              <button type="button" className="btn btn-ghost" onClick={() => { setShowTerms(true); setScrolledToEnd(false); }}>View</button>
            </div>

            {/* Submit */}
            {successNotice && (
              <div style={{ background: '#DCFCE7', border: '1px solid #22C55E', color: '#166534', padding: '12px 16px', borderRadius: 8, fontSize: 14, fontWeight: 500 }}>
                {successNotice}
              </div>
            )}
            <button type="submit" disabled={submitting} className="btn btn-primary" style={{ width: '100%', padding: '12px 16px', fontSize: 16, fontWeight: 600, display: 'inline-flex', justifyContent: 'center', alignItems: 'center', gap: 8 }}>
              {submitting ? (
                <>
                  <span className="btn-spinner" aria-hidden="true" />
                  Creating…
                </>
              ) : 'Create account'}
            </button>

            {/* Additional actions */}
            {offerSignin && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button type="button" onClick={handleSigninNow} className="btn btn-secondary" disabled={actionBusy}>
                  {actionBusy ? 'Signing…' : 'Sign in with this password'}
                </button>
                <a href="/login">Go to login</a>
              </div>
            )}
            {emailNeedsConfirm && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button type="button" onClick={handleResendConfirmation} className="btn btn-ghost" disabled={actionBusy}>
                  {actionBusy ? 'Resending…' : 'Resend confirmation email'}
                </button>
              </div>
            )}
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <a href="/login">Already have an account? Sign in</a>
            </div>
          </div>
        </form>
      </div>

      {showTerms && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal-card">
            <h3 style={{ margin: '4px 0 10px' }}>Terms, Acceptable Use, and Safety</h3>
            <TermsContent ref={termsRef} onScroll={handleTermsScroll} className="modal-scroll" />
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => { setShowTerms(false); }}>Close</button>
              <button
                className="btn btn-primary"
                disabled={!scrolledToEnd}
                onClick={() => { setAgree(true); setShowTerms(false); }}
                title={!scrolledToEnd ? 'Scroll to the end to enable' : 'Agree to Terms'}
              >
                {scrolledToEnd ? 'I Agree' : 'Read to the end to Agree'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterPage;
